import React, { useState, useEffect, useCallback, useRef, useMemo, useContext, createContext } from 'react';
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Typography,
  Space,
  Card,
  Divider,
  Tag,
  Modal,
  Form,
  message,
  Tooltip,
  Row,
  Col,
  Dropdown,
  Menu,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ShoppingOutlined,
  ToolOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SearchOutlined,
  SaveOutlined,
  StarOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseOutlined,
  RobotOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { EstimateLineItem, Part, LineItemTemplate, PartSavedTemplate, ShopSettings, Vehicle } from '../types';
import { lineItemTemplateApi, partSavedTemplateApi, shopSettingsApi } from '../services/api';
import { logEvent, newEventId } from '../services/eventLogger';
import LineItemProcedureEditor from './LineItemProcedureEditor';
import { formatCurrency } from '../utils/formatCurrency';

const { Text, Title } = Typography;
const { TextArea } = Input;


// ─── Edit Row Context ────────────────────────────────────────────────────────
// Provides volatile data to the module-level CustomRow component so its
// identity never changes between renders (preventing Ant Design Table from
// unmounting/remounting rows and killing input focus).
interface EditRowContextValue {
  items: EstimateLineItem[];
  editingKey: string;
  form: any;
  isMobile: boolean;
  disabled: boolean;
  discountType: 'percentage' | 'amount' | 'final_price';
  setDiscountType: (t: 'percentage' | 'amount' | 'final_price') => void;
  defaultLaborRate: number;
  save: (id: number | string) => Promise<void>;
  cancel: () => void;
  recalculateLineTotalOnChange: () => void;
  columns: any[];
}

const EditRowContext = createContext<EditRowContextValue>(null!);

// ─── Static data ─────────────────────────────────────────────────────────────
const predefinedCategories = [
  'Diagnostic', 'Maintenance', 'Brakes', 'Electrical', 'Safety', 'Transmission',
  'Engine', 'Cooling', 'Suspension', 'Exhaust', 'Body Work', 'Interior', 'Custom'
];

// ─── Module-scope CustomRow ──────────────────────────────────────────────────
// Extracted to module scope so its identity is stable across parent re-renders.
// Reads volatile data via EditRowContext instead of closure capture.
const CustomRow = (props: any) => {
  const ctx = useContext(EditRowContext);
  if (!ctx) return <tr {...props} />;
  const { items, editingKey, form, isMobile, disabled, discountType, setDiscountType, defaultLaborRate, save, cancel, recalculateLineTotalOnChange, columns } = ctx;

  const record = items.find(item => (item.id || 'new').toString() === props['data-row-key']);
  const editable = record && (record.id || 'new').toString() === editingKey;

  if (editable) {
    const recordKey = (record.id || 'new').toString();
    const baseLaborCost = (record.labor_hours || 0) * (record.hourly_rate || 0);
    const laborDiscountAmount = record.labor_discount_amount ||
      (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
    const discountedLaborCost = baseLaborCost - laborDiscountAmount;
    const partsCost = (record.parts || []).reduce((sum: number, part: Part) => sum + (part.discounted_price || part.total_price), 0);
    const totalPrice = discountedLaborCost + partsCost;

    return (
      <tr {...props} className="editing-row">
        <td colSpan={columns.length} style={{ padding: 0 }}>
          <div style={{
            background: '#fff',
            border: '2px solid #3b82f6',
            borderRadius: 12,
            margin: isMobile ? 8 : 12,
            padding: isMobile ? 16 : 24,
            boxShadow: '0 4px 12px rgba(59,130,246,0.08)',
          }}>
            {/* Row 1: Category + Description */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16, marginBottom: 16 }}>
              <div style={{ width: isMobile ? '100%' : 200, flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Category</div>
                <Form.Item name="category" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
                  <Select placeholder="Select category" size="large" style={{ width: '100%' }}>
                    {predefinedCategories.map(cat => (
                      <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Service Description</div>
                <Form.Item name="description" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
                  <Input placeholder="e.g. Brake pad replacement, front and rear" size="large" />
                </Form.Item>
              </div>
            </div>

            {/* Row 2: Labor fields — responsive grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : '80px 1fr 1fr 1fr',
              gap: isMobile ? 12 : 16,
              marginBottom: 16,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Qty</div>
                <Form.Item name="quantity" style={{ margin: 0 }} rules={[{ required: true, message: 'Req' }]}>
                  <InputNumber min={1} style={{ width: '100%' }} size="large" controls={false} />
                </Form.Item>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Labor Hours</div>
                <Form.Item name="labor_hours" style={{ margin: 0 }}>
                  <InputNumber
                    min={0} step={0.1} precision={1} style={{ width: '100%' }}
                    placeholder="0.0" size="large" controls={false} suffix="h"
                    onBlur={() => recalculateLineTotalOnChange()}
                  />
                </Form.Item>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Hourly Rate</div>
                <Form.Item name="hourly_rate" style={{ margin: 0 }}>
                  <InputNumber
                    min={0} step={5} style={{ width: '100%' }}
                    placeholder={`$${(defaultLaborRate || 0).toFixed(2)}`} size="large" controls={false} prefix="$"
                    onBlur={() => recalculateLineTotalOnChange()}
                  />
                </Form.Item>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Labor Discount
                  <span style={{ marginLeft: 8 }}>
                    <Button size="small" type={discountType === 'percentage' ? 'primary' : 'default'} onClick={() => { setDiscountType('percentage'); form.setFieldValue('labor_discount_amount', 0); form.setFieldValue('labor_discount_final_price', undefined); }} style={{ height: 22, fontSize: 11, padding: '0 6px', borderRadius: 4 }}>%</Button>
                    {' '}
                    <Button size="small" type={discountType === 'amount' ? 'primary' : 'default'} onClick={() => { setDiscountType('amount'); form.setFieldValue('labor_discount_percentage', 0); form.setFieldValue('labor_discount_final_price', undefined); }} style={{ height: 22, fontSize: 11, padding: '0 6px', borderRadius: 4 }}>$</Button>
                    {' '}
                    <Button size="small" type={discountType === 'final_price' ? 'primary' : 'default'} onClick={() => { setDiscountType('final_price'); form.setFieldValue('labor_discount_percentage', 0); form.setFieldValue('labor_discount_amount', 0); }} style={{ height: 22, fontSize: 11, padding: '0 6px', borderRadius: 4 }}>Final</Button>
                  </span>
                </div>
                {discountType === 'percentage' ? (
                  <Form.Item name="labor_discount_percentage" style={{ margin: 0 }}>
                    <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} size="large" placeholder="0" suffix="%" controls={false} onBlur={() => recalculateLineTotalOnChange()} onChange={(v) => { if (v) form.setFieldValue('labor_discount_amount', 0); }} />
                  </Form.Item>
                ) : discountType === 'final_price' ? (
                  <Form.Item name="labor_discount_final_price" style={{ margin: 0 }}>
                    <InputNumber min={0} step={1} style={{ width: '100%' }} size="large" placeholder="Final price" prefix="$" controls={false} onBlur={() => recalculateLineTotalOnChange()} />
                  </Form.Item>
                ) : (
                  <Form.Item name="labor_discount_amount" style={{ margin: 0 }}>
                    <InputNumber min={0} step={1} style={{ width: '100%' }} size="large" placeholder="0.00" prefix="$" controls={false} onBlur={() => recalculateLineTotalOnChange()} onChange={(v) => { if (v) form.setFieldValue('labor_discount_percentage', 0); }} />
                  </Form.Item>
                )}
              </div>
            </div>

            {/* Row 3: Notes (optional) */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></div>
              <Form.Item name="notes" style={{ margin: 0 }}>
                <Input.TextArea placeholder="Internal notes about this service..." rows={2} size="large" />
              </Form.Item>
            </div>

            {/* Footer: Total + Actions */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderTop: '1px solid #e5e7eb', paddingTop: 16,
            }}>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Line Total</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>
                  {formatCurrency(totalPrice)}
                </div>
                {laborDiscountAmount > 0 && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    <span style={{ textDecoration: 'line-through' }}>{formatCurrency(baseLaborCost)}</span>
                    {' '}<span style={{ color: '#10b981' }}>saved {formatCurrency(laborDiscountAmount)}</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button size="large" onClick={cancel}>
                  Cancel
                </Button>
                <Button type="primary" size="large" onClick={() => save(recordKey)} style={{ minWidth: 100 }}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return <tr {...props} />;
};

interface ServiceItemsTableProps {
  items: EstimateLineItem[];
  onChange: (items: EstimateLineItem[]) => void;
  disabled?: boolean;
  taxRate?: number;
  vehicle?: Vehicle;
}

interface PartSavedTemplateModalProps {
  open: boolean;
  onCancel: () => void;
  onSelect: (template: PartSavedTemplate) => void;
  vehicle?: Vehicle;
}

interface LineItemSavedTemplateModalProps {
  open: boolean;
  onCancel: () => void;
  onSelect: (template: LineItemTemplate) => void;
  vehicle?: Vehicle;
}

// ─── Internal Job Steps Panel ─────────────────────────────────────────────────

interface InternalJobStepsPanelProps {
  steps: Array<{ step: string; hours: number }>;
  lineItem: EstimateLineItem;
  itemIndex: number;
  onLaborHoursChange?: (index: number, newTotal: number) => void;
  readOnly?: boolean;
}

const InternalJobStepsPanel: React.FC<InternalJobStepsPanelProps> = ({
  steps,
  lineItem,
  itemIndex,
  onLaborHoursChange,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // collapsed by default
  const [localSteps, setLocalSteps] = useState<Array<{ step: string; hours: number }>>(steps);

  useEffect(() => {
    setLocalSteps(steps);
  }, [steps]);

  const totalHours = localSteps.reduce((sum, s) => sum + (s.hours || 0), 0);

  const handleHoursChange = (idx: number, newHours: number) => {
    const updated = localSteps.map((s, i) => i === idx ? { ...s, hours: newHours } : s);
    setLocalSteps(updated);
    const newTotal = updated.reduce((sum, s) => sum + (s.hours || 0), 0);
    if (onLaborHoursChange) {
      onLaborHoursChange(itemIndex, Math.round(newTotal * 100) / 100);
    }
  };

  if (localSteps.length === 0) return null;

  return (
    <div
      style={{
        margin: '8px 0 12px 0',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#f8fafc',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '7px 12px',
          background: '#f1f5f9',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#64748b', fontSize: 12, fontWeight: 500 }}>
            {isExpanded ? '▾' : '▸'}
          </span>
          <span style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
            Job Steps
          </span>
          <span
            style={{
              background: '#475569',
              color: '#fff',
              borderRadius: 10,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {localSteps.length} steps
          </span>
          <span
            style={{
              background: '#64748b',
              color: '#fff',
              borderRadius: 10,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {totalHours.toFixed(1)}h total
          </span>
        </div>
        <Button
          type="text"
          size="small"
          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
          style={{ color: '#64748b' }}
        />
      </div>

      {/* Steps list */}
      {isExpanded && (
        <div style={{ padding: '8px 14px 12px 14px' }}>
          {/* Column headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 90px',
              gap: 8,
              padding: '4px 0',
              marginBottom: 4,
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Step Description
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
              Hours
            </span>
          </div>

          {localSteps.map((s, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 90px',
                gap: 8,
                padding: '5px 0',
                borderBottom: idx < localSteps.length - 1 ? '1px solid #f1f5f9' : 'none',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span
                  style={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#94a3b8',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {idx + 1}
                </span>
                <span style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>
                  {s.step}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                {readOnly ? (
                  <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                    {s.hours.toFixed(1)}h
                  </span>
                ) : (
                  <InputNumber
                    size="small"
                    min={0}
                    max={40}
                    step={0.1}
                    precision={1}
                    value={s.hours}
                    onChange={(val) => handleHoursChange(idx, val ?? 0)}
                    style={{ width: 70 }}
                    suffix="h"
                  />
                )}
              </div>
            </div>
          ))}

          {/* Summary row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 90px',
              gap: 8,
              padding: '6px 0 2px 0',
              marginTop: 4,
              borderTop: '2px solid #cbd5e1',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
              Total Estimated Hours
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', textAlign: 'right' }}>
              {totalHours.toFixed(1)}h
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

interface PartsSectionProps {
  lineItem: EstimateLineItem;
  onChange: (lineItem: EstimateLineItem) => void;
  disabled?: boolean;
  vehicle?: Vehicle;
}

const PartsSection: React.FC<PartsSectionProps> = ({
  lineItem,
  onChange,
  disabled = false,
  vehicle,
}) => {
  const [editingPartKey, setEditingPartKey] = useState<string>('');
  const [savedPartTemplateModalVisible, setSavedPartTemplateModalVisible] = useState(false);
  const [deletePartModalVisible, setDeletePartModalVisible] = useState(false);
  const [partToDelete, setPartToDelete] = useState<string>('');
  const [partDiscountType, setPartDiscountType] = useState<'percentage' | 'amount' | 'final_price'>('percentage');
  const [savePartTemplateModalVisible, setSavePartTemplateModalVisible] = useState(false);
  const [partToSave, setPartToSave] = useState<Part | null>(null);
  const [partForm] = Form.useForm();
  const [savePartTemplateForm] = Form.useForm();

  // Check if last part is empty (newly added) and auto-edit it
  useEffect(() => {
    if (lineItem.parts && lineItem.parts.length > 0) {
      const lastPart = lineItem.parts[lineItem.parts.length - 1];
      // If the last part has no part_number and no description, it's a newly added empty part
      if (!lastPart.part_number && !lastPart.description && !editingPartKey) {
        console.log('Auto-editing new empty part:', lastPart);
        editPart(lastPart);
      }
    }
  }, [lineItem.parts?.length]); // Only run when parts array length changes

  const isEditingPart = (part: Part) => (part.id || 'new').toString() === editingPartKey;

  const editPart = (part: Part) => {
    partForm.setFieldsValue({
      part_number: part.part_number,
      description: part.description,
      quantity: part.quantity,
      unit_price: part.unit_price,
      discount_percentage: part.discount_percentage,
      discount_amount: part.discount_amount,
      availability: part.availability,
      supplier: part.supplier,
      notes: part.notes,
    });
    setEditingPartKey((part.id || 'new').toString());
  };

  const cancelPartEdit = () => {
    // If the part being edited has empty part_number and description, it's a new unsaved part - delete it
    const editingPart = (lineItem.parts || []).find(part => (part.id || 'new').toString() === editingPartKey);

    if (editingPart && !editingPart.part_number && !editingPart.description) {
      // This is a new part that was never saved - delete it
      const filteredParts = (lineItem.parts || []).filter(part => (part.id || 'new').toString() !== editingPartKey);
      const laborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
      const partsCost = filteredParts.reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);
      const totalServicePrice = laborCost + partsCost;

      onChange({
        ...lineItem,
        parts: filteredParts,
        total_price: totalServicePrice,
      });
    }
    // Otherwise it's an existing part - just cancel the edit (revert happens automatically)

    setEditingPartKey('');
    partForm.resetFields();
  };

  const savePartEdit = async (id: number | string) => {
    try {
      const values = await partForm.validateFields();
      const updatedParts = [...(lineItem.parts || [])];
      const partIndex = updatedParts.findIndex(part => (part.id || 'new').toString() === id.toString());

      if (partIndex > -1) {
        const part = updatedParts[partIndex];
        const unitPrice = values.unit_price || 0;
        const quantity = values.quantity || 1;
        const baseTotal = unitPrice * quantity;

        const discountAmount = values.discount_amount || 0;
        const discountPercentage = values.discount_percentage || 0;
        const discountFinalPrice = values.discount_final_price;

        // Calculate final discounted price with rounding
        let finalDiscountAmount: number;
        let discountedPrice: number;
        if (discountFinalPrice != null && discountFinalPrice > 0) {
          // "Set Final Price" mode — user typed the target price
          discountedPrice = Number(discountFinalPrice.toFixed(2));
          finalDiscountAmount = Number((baseTotal - discountedPrice).toFixed(2));
        } else {
          finalDiscountAmount = Number((discountAmount || (baseTotal * discountPercentage / 100)).toFixed(2));
          discountedPrice = Number((baseTotal - finalDiscountAmount).toFixed(2));
        }

        updatedParts[partIndex] = {
          ...part,
          ...values,
          total_price: baseTotal,
          discount_amount: finalDiscountAmount,
          discount_percentage: discountPercentage,
          discounted_price: discountedPrice,
        };
      }

      const baseLaborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
      const laborDiscountAmt = lineItem.labor_discount_amount ||
        (baseLaborCost * (lineItem.labor_discount_percentage || 0) / 100);
      const laborCost = baseLaborCost - laborDiscountAmt;
      const partsCost = updatedParts.reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);
      const totalServicePrice = Number((laborCost + partsCost).toFixed(2));

      onChange({
        ...lineItem,
        parts: updatedParts,
        total_price: totalServicePrice,
      });

      setEditingPartKey('');
      partForm.resetFields();
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const addNewPart = () => {
    const newPart: Part = {
      id: Date.now(),
      line_item_id: lineItem.id,
      part_number: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      discount_percentage: 0,
      discount_amount: 0,
      discounted_price: 0,
      availability: 'in_stock',
    };
    const updatedParts = [...(lineItem.parts || []), newPart];

    onChange({
      ...lineItem,
      parts: updatedParts,
    });
    // The useEffect will automatically detect the new empty part and edit it
  };

  const addFromSavedPartTemplate = (template: PartSavedTemplate) => {
    const newPart: Part = {
      id: Date.now(),
      line_item_id: lineItem.id,
      part_number: template.part_number,
      description: template.description,
      quantity: 1,
      unit_price: template.unit_price,
      total_price: template.unit_price,
      discount_percentage: template.discount_percentage || 0,
      discount_amount: template.discount_amount || 0,
      discounted_price: template.unit_price - (template.discount_amount || 0),
      availability: template.availability,
      supplier: template.supplier,
      notes: template.notes,
    };

    const updatedParts = [...(lineItem.parts || []), newPart];
    const laborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
    const partsCost = updatedParts.reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);
    const totalServicePrice = laborCost + partsCost;

    onChange({
      ...lineItem,
      parts: updatedParts,
      total_price: totalServicePrice,
    });

    setSavedPartTemplateModalVisible(false);
    message.success(`Added ${template.name} to parts list`);
  };

  const savePartAsTemplate = (part: Part) => {
    setPartToSave(part);
    savePartTemplateForm.setFieldsValue({
      name: `${part.description} - Part Template`,
      isGeneral: !vehicle, // Default to general if no vehicle, vehicle-specific if vehicle exists
      vehicleYears: vehicle ? [vehicle.year] : []
    });
    setSavePartTemplateModalVisible(true);
  };

  // Generate year options from 1990 to current year + 2
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear + 2 - 1990 + 1 }, (_, i) => 1990 + i);

  const handleSavePartTemplate = async () => {
    try {
      if (!partToSave) return;

      const values = await savePartTemplateForm.validateFields();

      const template: Omit<PartSavedTemplate, 'id'> = {
        name: values.name,
        part_number: partToSave.part_number,
        description: partToSave.description,
        unit_price: partToSave.unit_price,
        discount_percentage: partToSave.discount_percentage,
        discount_amount: partToSave.discount_amount,
        availability: partToSave.availability,
        supplier: partToSave.supplier,
        notes: partToSave.notes,
      };

      // Add vehicle information if not marked as general
      if (!values.isGeneral && vehicle && values.vehicleYears && values.vehicleYears.length > 0) {
        template.vehicle_years = values.vehicleYears.join(',');
        template.vehicle_make = vehicle.make;
        template.vehicle_model = vehicle.model;
      }

      await partSavedTemplateApi.create(template);
      message.success(`Part template "${values.name}" saved successfully!`);
      setSavePartTemplateModalVisible(false);
      setPartToSave(null);
      savePartTemplateForm.resetFields();
    } catch (error) {
      console.error('Error saving part template:', error);
      message.error('Failed to save part template');
    }
  };

  const deletePart = (id: number | string) => {
    console.log('Deleting part with id:', id);
    console.log('Current parts:', lineItem.parts);
    const filteredParts = (lineItem.parts || []).filter(part => (part.id || 'new').toString() !== id.toString());
    console.log('Filtered parts:', filteredParts);
    const laborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
    const partsCost = filteredParts.reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);
    const totalServicePrice = laborCost + partsCost;

    onChange({
      ...lineItem,
      parts: filteredParts,
      total_price: totalServicePrice,
    });
  };

  const handleConfirmDeletePart = () => {
    console.log('User confirmed delete');
    deletePart(partToDelete);
    setDeletePartModalVisible(false);
    setPartToDelete('');
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'green';
      case 'order_required': return 'orange';
      case 'backordered': return 'red';
      case 'discontinued': return 'default';
      default: return 'default';
    }
  };

  const getAvailabilityIcon = (availability: string) => {
    switch (availability) {
      case 'in_stock': return <CheckCircleOutlined />;
      case 'order_required': return <ClockCircleOutlined />;
      case 'backordered': return <ExclamationCircleOutlined />;
      case 'discontinued': return <StopOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'In Stock';
      case 'order_required': return 'Order Required';
      case 'backordered': return 'Backordered';
      case 'discontinued': return 'Discontinued';
      default: return availability;
    }
  };

  const partColumns = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      width: 90,
      render: (partNumber: string, record: Part) => {
        if (isEditingPart(record)) {
          return (
            <Form.Item
              name="part_number"
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Part number is required' }]}
            >
              <Input placeholder="P12345" size="small" style={{ fontSize: '12px', minWidth: '60px' }} />
            </Form.Item>
          );
        }
        return (
          <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {partNumber || '—'}
          </Text>
        );
      },
    },
    {
      title: 'Part Description',
      dataIndex: 'description',
      width: 130,
      render: (description: string, record: Part) => {
        if (isEditingPart(record)) {
          return (
            <Form.Item
              name="description"
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Description is required' }]}
            >
              <Input placeholder="Part description" size="small" style={{ fontSize: '12px', minWidth: '140px' }} />
            </Form.Item>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: '500', fontSize: '12px', wordWrap: 'break-word', lineHeight: '1.3' }}>
              {description}
              {record.is_ai_suggested && (
                <Tag
                  color="geekblue"
                  style={{ marginLeft: '4px', fontSize: '9px', padding: '0 3px', lineHeight: '16px', verticalAlign: 'middle' }}
                >
                  AI ✦
                </Tag>
              )}
              {record.is_optional && (
                <Tag
                  color="orange"
                  style={{ marginLeft: '2px', fontSize: '9px', padding: '0 3px', lineHeight: '16px', verticalAlign: 'middle' }}
                >
                  Optional
                </Tag>
              )}
            </div>
            {record.supplier && (
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '2px' }}>
                {record.supplier}
              </Text>
            )}
            {record.is_ai_suggested && typeof record.ai_confidence === 'number' && (
              <Text
                type="secondary"
                style={{
                  fontSize: '10px',
                  display: 'block',
                  marginTop: '1px',
                  color: record.ai_confidence >= 0.7 ? '#10b981' : record.ai_confidence >= 0.5 ? '#f59e0b' : '#ef4444',
                }}
              >
                {Math.round(record.ai_confidence * 100)}% confidence
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 50,
      render: (quantity: number, record: Part) => {
        if (isEditingPart(record)) {
          return (
            <Form.Item
              name="quantity"
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Quantity is required' }]}
            >
              <InputNumber min={1} style={{ width: '100%', minWidth: '45px' }} size="small" controls={false} />
            </Form.Item>
          );
        }
        return quantity;
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      width: 70,
      render: (price: number, record: Part) => {
        if (isEditingPart(record)) {
          return (
            <Form.Item
              name="unit_price"
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Price is required' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%', minWidth: '75px' }}
                size="small"
                controls={false}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>
          );
        }
        // AI-suggested parts with no price set show a placeholder instead of $0.00
        if (record.is_ai_suggested && (price === 0 || price == null)) {
          return (
            <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
              — set price
            </Text>
          );
        }
        return formatCurrency(price);
      },
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      width: 80,
      render: (_: any, record: Part) => {
        if (isEditingPart(record)) {
          return (
            <Form.Item
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <Button
                    size="small"
                    type={partDiscountType === 'percentage' ? 'primary' : 'default'}
                    onClick={() => {
                      setPartDiscountType('percentage');
                      partForm.setFieldValue('discount_amount', 0);
                    }}
                    style={{ height: '16px', fontSize: '10px', padding: '0 3px', minWidth: '20px' }}
                  >
                    %
                  </Button>
                  <Button
                    size="small"
                    type={partDiscountType === 'amount' ? 'primary' : 'default'}
                    onClick={() => {
                      setPartDiscountType('amount');
                      partForm.setFieldValue('discount_percentage', 0);
                    }}
                    style={{ height: '16px', fontSize: '10px', padding: '0 3px', minWidth: '20px' }}
                  >
                    $
                  </Button>
                  <Button
                    size="small"
                    type={partDiscountType === 'final_price' ? 'primary' : 'default'}
                    onClick={() => {
                      setPartDiscountType('final_price');
                      partForm.setFieldValue('discount_percentage', 0);
                      partForm.setFieldValue('discount_amount', 0);
                    }}
                    style={{ height: '16px', fontSize: '10px', padding: '0 3px', minWidth: '30px' }}
                  >
                    Final
                  </Button>
                </div>
              }
              style={{ margin: 0 }}
            >
              {partDiscountType === 'percentage' ? (
                <Form.Item name="discount_percentage" noStyle>
                  <InputNumber
                    min={0}
                    max={100}
                    step={1}
                    style={{ width: '100%', minWidth: '60px' }}
                    size="small"
                    placeholder="0"
                    suffix="%"
                    controls={false}
                  />
                </Form.Item>
              ) : partDiscountType === 'final_price' ? (
                <Form.Item name="discount_final_price" noStyle>
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: '100%', minWidth: '60px' }}
                    size="small"
                    placeholder="Charge"
                    controls={false}
                    prefix="$"
                  />
                </Form.Item>
              ) : (
                <Form.Item name="discount_amount" noStyle>
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: '100%', minWidth: '60px' }}
                    size="small"
                    placeholder="0"
                    controls={false}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
                  />
                </Form.Item>
              )}
            </Form.Item>
          );
        }

        const hasDiscount = record.discount_percentage || record.discount_amount;
        if (!hasDiscount) return '—';

        return (
          <div style={{ fontSize: '11px' }}>
            {record.discount_percentage && (
              <div style={{ color: '#ef4444' }}>{record.discount_percentage.toFixed(1)}%</div>
            )}
            {record.discount_amount && (
              <div style={{ color: '#ef4444' }}>-{formatCurrency(record.discount_amount).substring(1)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      width: 70,
      render: (_: any, record: Part) => {
        const baseTotal = record.unit_price * record.quantity;
        const discountAmount = record.discount_amount || 0;
        const finalPrice = record.discounted_price || (baseTotal - discountAmount);

        if (discountAmount > 0) {
          return (
            <div style={{ fontSize: '11px' }}>
              <div style={{ textDecoration: 'line-through', color: '#999' }}>
                {formatCurrency(baseTotal)}
              </div>
              <div style={{ color: '#10b981', fontWeight: '600' }}>
                {formatCurrency(finalPrice)}
              </div>
            </div>
          );
        }

        return (
          <div style={{ color: '#10b981', fontWeight: '600', fontSize: '12px' }}>
            {formatCurrency(finalPrice)}
          </div>
        );
      },
    },
    {
      title: 'Availability',
      dataIndex: 'availability',
      width: 90,
      render: (availability: string, record: Part) => {
        if (isEditingPart(record)) {
          return (
            <Form.Item
              name="availability"
              style={{ margin: 0 }}
              rules={[{ required: true, message: 'Availability is required' }]}
            >
              <Select style={{ width: '100%' }} size="small">
                <Select.Option value="in_stock">In Stock</Select.Option>
                <Select.Option value="order_required">Order Required</Select.Option>
                <Select.Option value="backordered">Backordered</Select.Option>
                <Select.Option value="discontinued">Discontinued</Select.Option>
              </Select>
            </Form.Item>
          );
        }
        return (
          <Tag
            icon={getAvailabilityIcon(availability)}
            color={getAvailabilityColor(availability)}
            style={{ fontSize: '9px' }}
          >
            {availability === 'in_stock' ? 'In Stock' :
             availability === 'order_required' ? 'Order Req.' :
             availability === 'backordered' ? 'Backorder' :
             availability === 'discontinued' ? 'Disc.' : availability}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      width: 50,
      render: (text: any, record: Part) => {
        const editable = isEditingPart(record);
        const recordKey = (record.id || 'new').toString();

        if (editable) {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                onClick={() => savePartEdit(recordKey)}
              >
                Save
              </Button>
              <Button size="small" onClick={cancelPartEdit}>
                Cancel
              </Button>
            </Space>
          );
        }

        const handleMenuClick = (e: { key: string }) => {
          console.log('Part menu clicked:', e.key, 'for record:', recordKey);

          switch (e.key) {
            case 'edit':
              editPart(record);
              break;
            case 'save-template':
              savePartAsTemplate(record);
              break;
            case 'delete':
              console.log('Delete menu item clicked');
              cancelPartEdit();
              setPartToDelete(recordKey);
              setDeletePartModalVisible(true);
              break;
          }
        };

        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
          },
          {
            key: 'save-template',
            icon: <SaveOutlined />,
            label: 'Save as Template',
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={['click']}
            disabled={disabled}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              disabled={disabled}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ backgroundColor: '#f8fafc', padding: '12px 16px', margin: '0 auto', maxWidth: '95%', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <Text strong style={{ color: '#374151', fontSize: '13px', fontWeight: 600 }}>Parts ({(lineItem.parts || []).length})</Text>
        </div>
        <Space size="small">
          <Button
            type="default"
            size="small"
            icon={<StarOutlined />}
            onClick={() => setSavedPartTemplateModalVisible(true)}
            disabled={disabled}
            style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
          >
            Saved
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={addNewPart}
            disabled={disabled}
            style={{
              backgroundColor: '#10b981',
              borderColor: '#10b981',
            }}
          >
            Add
          </Button>
        </Space>
      </div>
      <Form form={partForm} component={false}>
        <Table
          columns={partColumns}
          dataSource={lineItem.parts || []}
          rowKey={record => String(record.id ?? record.part_number ?? record.description ?? 'new-part')}
          pagination={false}
          size="small"
          scroll={{ x: window.innerWidth < 768 ? 500 : undefined }}
          locale={{
            emptyText: (
              <div style={{ padding: '12px', textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>No parts</Text>
              </div>
            ),
          }}
        />
      </Form>

      <PartSavedTemplateModal
        open={savedPartTemplateModalVisible}
        onCancel={() => setSavedPartTemplateModalVisible(false)}
        onSelect={addFromSavedPartTemplate}
        vehicle={vehicle}
      />
      <Modal
        title="Save Part as Template"
        open={savePartTemplateModalVisible}
        onOk={handleSavePartTemplate}
        onCancel={() => {
          setSavePartTemplateModalVisible(false);
          setPartToSave(null);
          savePartTemplateForm.resetFields();
        }}
        okText="Save Template"
        cancelText="Cancel"
        centered
        width={500}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Save this part as a reusable template{vehicle ? ` for ${vehicle.year} ${vehicle.make} ${vehicle.model}` : ''}. You can use it later in any estimate.
          </Text>
        </div>
        {partToSave && (
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '1px solid #e5e7eb'
          }}>
            <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Part Details:
            </Text>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              <div><strong>Part Number:</strong> {partToSave.part_number}</div>
              <div><strong>Description:</strong> {partToSave.description}</div>
              <div><strong>Unit Price:</strong> {formatCurrency(partToSave.unit_price)}</div>
              <div><strong>Availability:</strong> {partToSave.availability}</div>
              {vehicle && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                  <div><strong>Vehicle:</strong> {vehicle.year} {vehicle.make} {vehicle.model}</div>
                  {vehicle.vin && <div><strong>VIN:</strong> {vehicle.vin}</div>}
                </div>
              )}
            </div>
          </div>
        )}
        <Form form={savePartTemplateForm} layout="vertical">
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: 'Please enter a template name' },
              { min: 1, message: 'Template name must not be empty' }
            ]}
          >
            <Input placeholder="Enter a name for this template" autoFocus />
          </Form.Item>
          <Form.Item
            name="isGeneral"
            valuePropName="checked"
            help={!vehicle ? undefined : "If unchecked, this template will only appear when working with similar vehicles"}
          >
            <Checkbox>
              Make this a general template (available for all vehicles)
            </Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isGeneral !== currentValues.isGeneral}
          >
            {({ getFieldValue }) =>
              !getFieldValue('isGeneral') && vehicle ? (
                <Form.Item
                  name="vehicleYears"
                  label="Vehicle Years"
                  rules={[{ required: true, message: 'Please select at least one year' }]}
                  help="Select all years this part fits"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select years"
                    showSearch
                    filterOption={(input, option) => {
                      const yearValue = option?.value;
                      if (typeof yearValue === 'number') {
                        return yearValue.toString().includes(input);
                      }
                      return false;
                    }}
                  >
                    {yearOptions.map(year => (
                      <Select.Option key={year} value={year}>
                        {year}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Delete Part"
        open={deletePartModalVisible}
        onOk={handleConfirmDeletePart}
        onCancel={() => {
          setDeletePartModalVisible(false);
          setPartToDelete('');
        }}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        centered
      >
        <p>Are you sure you want to delete this part? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

const ServiceItemsTable: React.FC<ServiceItemsTableProps> = ({
  items,
  onChange,
  disabled = false,
  taxRate = 7,
  vehicle,
}) => {
  const [editingKey, setEditingKey] = useState<string>('');
  const [savedTemplateModalVisible, setSavedTemplateModalVisible] = useState(false);
  const [deleteItemModalVisible, setDeleteItemModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [discountType, setDiscountType] = useState<'percentage' | 'amount' | 'final_price'>('percentage');
  const [defaultLaborRate, setDefaultLaborRate] = useState<number>(175);
  const [saveLineItemTemplateModalVisible, setSaveLineItemTemplateModalVisible] = useState(false);
  const [lineItemToSave, setLineItemToSave] = useState<EstimateLineItem | null>(null);
  // Track which rows have their Parts panel expanded
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  // Track in-progress hours edits for 'low' confidence pending items: itemId → draft hours value
  const [lowConfHoursDraft, setLowConfHoursDraft] = useState<Record<string, number | null>>({});
  // Track save-to-library checkbox state per item: itemId → boolean (default true)
  const [saveToLibraryChecked, setSaveToLibraryChecked] = useState<Record<string, boolean>>({});
  const [partsMarkup] = useState(40);
  const itemsRef = React.useRef(items);
  React.useEffect(() => { itemsRef.current = items; }, [items]);
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const [form] = Form.useForm();
  const [saveLineItemTemplateForm] = Form.useForm();

  // Load shop settings to get default labor rate
  useEffect(() => {
    const loadShopSettings = async () => {
      try {
        const response = await shopSettingsApi.get();
        setDefaultLaborRate(response.data.labor_rate);
      } catch (error) {
        console.error('Error loading shop settings:', error);
        // Keep default of 175 if API fails
      }
    };
    loadShopSettings();
  }, []);

  // Auto-expand Parts panel for any pending AI item that has parts
  const prevPendingKeysRef = useRef<string>('');
  useEffect(() => {
    const pendingWithParts = items
      .filter(i => i.approval_status === 'pending' && (i.parts || []).length > 0)
      .map(i => String(i.id ?? 'new'));
    const key = pendingWithParts.join(',');
    if (key && key !== prevPendingKeysRef.current) {
      prevPendingKeysRef.current = key;
      setExpandedRowKeys(prev => {
        const combined = Array.from(new Set([...prev, ...pendingWithParts]));
        return combined;
      });
    }
  }, [items]);

  // Real-time calculation function for labor costs
  const recalculateLineTotalOnChange = () => {
    if (editingKey) {
      const formValues = form.getFieldsValue();
      const currentRecord = items.find(item => (item.id || 'new').toString() === editingKey);

      if (currentRecord && formValues) {
        const baseLaborCost = (formValues.labor_hours || 0) * (formValues.hourly_rate || 0);
        let laborDiscountAmount: number;
        if (formValues.labor_discount_final_price != null && formValues.labor_discount_final_price > 0) {
          laborDiscountAmount = Math.max(0, baseLaborCost - formValues.labor_discount_final_price);
        } else {
          laborDiscountAmount = formValues.labor_discount_amount ||
            (baseLaborCost * (formValues.labor_discount_percentage || 0) / 100);
        }
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;
        const partsCost = (currentRecord.parts || []).reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);
        const newTotal = discountedLaborCost + partsCost;

        // Update the items array temporarily for display purposes
        const updatedItems = items.map(item => {
          if ((item.id || 'new').toString() === editingKey) {
            return {
              ...item,
              ...formValues,
              total_price: newTotal,
              labor_discount_amount: laborDiscountAmount,
            };
          }
          return item;
        });
        onChange(updatedItems);
      }
    }
  };

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsMobile(window.innerWidth < 768), 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  const isEditing = (record: EstimateLineItem) => (record.id || 'new').toString() === editingKey;

  const edit = (record: EstimateLineItem) => {
    form.setFieldsValue({
      category: record.category,
      description: record.description,
      quantity: record.quantity,
      labor_hours: record.labor_hours,
      hourly_rate: record.hourly_rate,
      labor_discount_percentage: record.labor_discount_percentage,
      labor_discount_amount: record.labor_discount_amount,
      notes: record.notes,
    });
    setEditingKey((record.id || 'new').toString());
  };

  const cancel = () => {
    // If the line item being edited has empty description, it's a new unsaved item - delete it
    const editingItem = items.find(item => (item.id || 'new').toString() === editingKey);

    if (editingItem && !editingItem.description) {
      // This is a new line item that was never saved - delete it
      const filteredItems = items.filter(item => (item.id || 'new').toString() !== editingKey);
      onChange(filteredItems);
    }
    // Otherwise it's an existing line item - just cancel the edit (revert happens automatically)

    setEditingKey('');
    form.resetFields();
  };

  const save = async (id: number | string) => {
    try {
      const values = await form.validateFields();
      const updatedItems = [...items];
      const index = updatedItems.findIndex(item => (item.id || 'new').toString() === id.toString());

      if (index > -1) {
        const item = updatedItems[index];
        const baseLaborCost = (values.labor_hours || 0) * (values.hourly_rate || 0);

        // Calculate labor discount
        let laborDiscountAmount: number;
        if (values.labor_discount_final_price != null && values.labor_discount_final_price > 0) {
          // "Set Final Price" — user typed the target labor price
          laborDiscountAmount = Math.max(0, baseLaborCost - values.labor_discount_final_price);
        } else {
          laborDiscountAmount = values.labor_discount_amount ||
            (baseLaborCost * (values.labor_discount_percentage || 0) / 100);
        }
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;

        const partsCost = (item.parts || []).reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);

        // For services: combine discounted labor + parts cost
        const totalPrice = discountedLaborCost + partsCost;

        updatedItems[index] = {
          ...item,
          ...values,
          total_price: totalPrice,
          labor_hours: values.labor_hours || 0,
          hourly_rate: values.hourly_rate || 0,
          labor_discount_percentage: values.labor_discount_percentage || 0,
          labor_discount_amount: laborDiscountAmount,
          // Saving a pending AI item implicitly approves it
          approval_status: item.approval_status === 'pending' ? 'approved' : item.approval_status,
        };
      }

      onChange(updatedItems);
      setEditingKey('');
      form.resetFields();
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const addNewItem = () => {
    const newItem: EstimateLineItem = {
      id: Date.now(), // Temporary ID
      category: 'Custom',
      description: '',
      quantity: 1,
      labor_hours: 0,
      hourly_rate: defaultLaborRate,
      labor_discount_percentage: 0,
      labor_discount_amount: 0,
      total_price: 0,
      parts: [],
    };
    onChange([...items, newItem]);
    edit(newItem);
  };

  const addFromSavedTemplate = (template: LineItemTemplate) => {
    // Use template's hourly rate if set, otherwise use default labor rate
    const hourlyRate = template.hourly_rate || defaultLaborRate;
    const baseLaborCost = (template.labor_hours || 0) * hourlyRate;
    const laborDiscountAmount = template.labor_discount_amount ||
      (baseLaborCost * (template.labor_discount_percentage || 0) / 100);
    const discountedLaborCost = baseLaborCost - laborDiscountAmount;

    const newItem: EstimateLineItem = {
      id: Date.now(),
      category: template.category,
      description: template.description,
      quantity: template.quantity,
      labor_hours: template.labor_hours,
      hourly_rate: hourlyRate,
      labor_discount_percentage: template.labor_discount_percentage,
      labor_discount_amount: laborDiscountAmount,
      total_price: discountedLaborCost,
      parts: [],
      notes: template.notes,
    };
    onChange([...items, newItem]);
    setSavedTemplateModalVisible(false);
    message.success(`Added ${template.name} to estimate`);
  };

  const saveLineItemAsTemplate = (lineItem: EstimateLineItem) => {
    // Validate required fields before showing modal
    if (!lineItem.category || lineItem.category.trim() === '') {
      message.error('Cannot save template: Category is required');
      return;
    }
    if (!lineItem.description || lineItem.description.trim() === '') {
      message.error('Cannot save template: Description is required');
      return;
    }
    if (!lineItem.quantity || lineItem.quantity <= 0) {
      message.error('Cannot save template: Quantity must be greater than 0');
      return;
    }
    if (!Number.isInteger(lineItem.quantity)) {
      message.error('Cannot save template: Quantity must be a whole number');
      return;
    }

    setLineItemToSave(lineItem);
    saveLineItemTemplateForm.setFieldsValue({
      name: `${lineItem.description} - Service Template`,
      isGeneral: !vehicle, // Default to general if no vehicle, vehicle-specific if vehicle exists
      vehicleYears: vehicle ? [vehicle.year] : []
    });
    setSaveLineItemTemplateModalVisible(true);
  };

  // Generate year options from 1990 to current year + 2
  const serviceItemsYearOptions = Array.from({ length: new Date().getFullYear() + 2 - 1990 + 1 }, (_, i) => 1990 + i);

  const handleSaveLineItemTemplate = async () => {
    try {
      if (!lineItemToSave) return;

      const values = await saveLineItemTemplateForm.validateFields();

      // Build template object with only defined optional fields
      const template: Omit<LineItemTemplate, 'id'> = {
        name: values.name.trim(),
        category: lineItemToSave.category.trim(),
        description: lineItemToSave.description.trim(),
        quantity: lineItemToSave.quantity,
      };

      // Add optional fields only if they have valid values
      if (lineItemToSave.labor_hours !== undefined && lineItemToSave.labor_hours !== null) {
        template.labor_hours = lineItemToSave.labor_hours;
      }
      if (lineItemToSave.hourly_rate !== undefined && lineItemToSave.hourly_rate !== null) {
        template.hourly_rate = lineItemToSave.hourly_rate;
      }
      if (lineItemToSave.labor_discount_percentage !== undefined && lineItemToSave.labor_discount_percentage !== null) {
        template.labor_discount_percentage = lineItemToSave.labor_discount_percentage;
      }
      if (lineItemToSave.labor_discount_amount !== undefined && lineItemToSave.labor_discount_amount !== null) {
        template.labor_discount_amount = lineItemToSave.labor_discount_amount;
      }
      if (lineItemToSave.notes !== undefined && lineItemToSave.notes !== null && lineItemToSave.notes.trim() !== '') {
        template.notes = lineItemToSave.notes.trim();
      }

      // Add vehicle information if not marked as general
      if (!values.isGeneral && vehicle && values.vehicleYears && values.vehicleYears.length > 0) {
        template.vehicle_years = values.vehicleYears.join(',');
        template.vehicle_make = vehicle.make;
        template.vehicle_model = vehicle.model;
      }

      await lineItemTemplateApi.create(template);
      message.success(`Line item template "${values.name}" saved successfully!`);
      setSaveLineItemTemplateModalVisible(false);
      setLineItemToSave(null);
      saveLineItemTemplateForm.resetFields();
    } catch (error) {
      console.error('Error saving line item template:', error);
      message.error('Failed to save line item template');
    }
  };

  const deleteItem = (id: number | string) => {
    console.log('Deleting line item with id:', id);
    console.log('Current items:', items);
    const filteredItems = items.filter(item => (item.id || 'new').toString() !== id.toString());
    console.log('Filtered items:', filteredItems);
    onChange(filteredItems);
  };

  /** Approve a single pending AI-suggested item (clears approval_status). */
  const approveItem = (id: number | string) => {
    const approvedItem = items.find(item => (item.id || 'new').toString() === id.toString());
    const updatedItems = items.map(item =>
      (item.id || 'new').toString() === id.toString()
        ? { ...item, approval_status: 'approved' as const }
        : item
    );
    onChange(updatedItems);

    if (approvedItem) {
      logEvent({
        event_id: newEventId(),
        event_type: 'estimate.service_item.approved',
        entity_type: 'estimate',
        source: 'ai_analyze',
        service_category: approvedItem.category,
        service_description: approvedItem.description?.substring(0, 200),
      });
    }
  };

  /** Approve all pending items at once.
   * - 'low' confidence items are SKIPPED (require manual hour entry).
   * - 'researched' confidence items are APPROVED but show an informational toast.
   * - 'verified' / 'estimated' items are approved silently.
   */
  const approveAllPending = () => {
    const lowConfCount = items.filter(
      item => item.approval_status === 'pending' && item.labor_confidence === 'low'
    ).length;
    const researchedCount = items.filter(
      item => item.approval_status === 'pending' && item.labor_confidence === 'researched'
    ).length;

    // Approve verified + estimated + researched; skip low
    const updatedItems = items.map(item =>
      item.approval_status === 'pending' && item.labor_confidence !== 'low'
        ? { ...item, approval_status: 'approved' as const }
        : item
    );
    onChange(updatedItems);

    if (lowConfCount > 0 && researchedCount > 0) {
      message.warning(
        `Approved items including ${researchedCount} AI-researched time${researchedCount > 1 ? 's' : ''}. ` +
        `${lowConfCount} item${lowConfCount > 1 ? 's' : ''} with unverified labor hours require manual confirmation. ` +
        `Visit Admin → Labor Times → AI Researched to verify researched entries.`
      );
    } else if (lowConfCount > 0) {
      message.warning(
        `Approved items with verified/estimated labor times. ${lowConfCount} item${lowConfCount > 1 ? 's' : ''} with unverified labor hours require manual confirmation.`
      );
    } else if (researchedCount > 0) {
      message.info(
        `All items approved. ${researchedCount} item${researchedCount > 1 ? 's' : ''} used AI-researched labor times. ` +
        `Visit Admin → Labor Times → AI Researched to verify.`
      );
    } else {
      message.success('All AI-suggested items approved');
    }
  };

  /** Save confirmed labor hours to the labor library for this vehicle context. */
  const saveLaborToLibrary = async (item: EstimateLineItem, confirmedHours: number): Promise<boolean> => {
    if (!vehicle?.make || !vehicle?.model || !vehicle?.year) return false;

    try {
      const { getTokens } = await import('../services/authApi');
      const tokens = getTokens();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (tokens?.accessToken) {
        headers['Authorization'] = `Bearer ${tokens.accessToken}`;
      }

      const body = {
        category: (item.category || 'other').toLowerCase().replace(/\s+/g, '_'),
        service_description: item.description,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        vehicle_year_from: vehicle.year,
        vehicle_year_to: vehicle.year,
        engine_size: vehicle.engine_size || null,
        transmission_type: null,
        flat_rate_hours: confirmedHours,
        notes: 'Advisor-confirmed from estimate',
      };

      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const resp = await fetch(`${apiUrl}/admin/labor-times`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (resp.status === 409) return false; // duplicate — caller handles
      return resp.ok;
    } catch {
      return false;
    }
  };

  /** Delete all pending items at once. */
  const deleteAllPending = () => {
    const filteredItems = items.filter(item => item.approval_status !== 'pending');
    onChange(filteredItems);
  };

  const handleConfirmDeleteItem = () => {
    console.log('User confirmed delete');
    deleteItem(itemToDelete);
    setDeleteItemModalVisible(false);
    setItemToDelete('');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'labor': return <ToolOutlined style={{ color: '#3b82f6' }} />;
      case 'part': return <ShoppingOutlined style={{ color: '#10b981' }} />;
      case 'service': return <SettingOutlined style={{ color: '#f59e0b' }} />;
      default: return <SettingOutlined />;
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Category',
      dataIndex: 'category',
      width: isMobile ? 75 : 110,
      render: (category: string, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        return (
          <Tag color="blue" style={{ fontSize: '10px' }}>
            {category}
          </Tag>
        );
      },
    },
    {
      title: 'Service Description',
      dataIndex: 'description',
      width: isMobile ? 130 : 200,
      render: (description: string, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        const progressText = record.notes;
        return (
          <div style={{ fontWeight: '500', fontSize: '13px', wordWrap: 'break-word', lineHeight: '1.3' }}>
            {description}
            {record.approval_status === 'pending' && (
              <Tag
                icon={<RobotOutlined />}
                color="blue"
                style={{ marginLeft: '6px', fontSize: '10px', verticalAlign: 'middle' }}
              >
                AI
              </Tag>
            )}
            {progressText && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', fontWeight: 'normal' }}>
                {progressText}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: isMobile ? 40 : 45,
      render: (quantity: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        return quantity;
      },
    },
    {
      title: 'Labor Hours',
      dataIndex: 'labor_hours',
      width: isMobile ? 65 : 100,
      render: (hours: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        const hoursDisplay = hours ? `${hours}h` : '—';

        // Show labor confidence badge only on pending AI-suggested items
        if (record.approval_status === 'pending' && record.labor_confidence) {
          const conf = record.labor_confidence;
          let badgeColor: string;
          let badgeLabel: string;
          let tooltipText: string;

          if (conf === 'verified') {
            badgeColor = '#10b981'; // green
            badgeLabel = '✓ verified';
            tooltipText = 'Labor time verified against your shop\'s reference database. High confidence.';
          } else if (conf === 'researched') {
            badgeColor = '#3b82f6'; // blue
            badgeLabel = '🔍 AI researched';
            const researchNotes = (record as any).labor_research_notes;
            tooltipText = researchNotes
              ? `AI-researched labor time (web + LLM). Source: ${researchNotes}. ` +
                'Review in Admin → Labor Times → AI Researched before promoting to library.'
              : 'Labor time sourced via AI web research (Perplexity/Groq). ' +
                'Review in Admin → Labor Times → AI Researched before promoting to library.';
          } else if (conf === 'low') {
            badgeColor = '#ef4444'; // red
            badgeLabel = '⚠ verify time';
            tooltipText =
              'AI has limited data for this specific job + vehicle combination. ' +
              'Enter the correct hours and confirm before approving.';
          } else {
            // 'estimated'
            badgeColor = '#f59e0b'; // amber
            badgeLabel = '~ estimated';
            tooltipText =
              'AI estimated this time from cross-referenced flat-rate sources (ALLDATA, Mitchell1, service manuals). ' +
              'Sources are in reasonable agreement for this job + vehicle. Review before approving.';
          }

          // For 'low' confidence items: show inline hours input + save-to-library toggle
          if (conf === 'low') {
            const itemKey = String(record.id ?? record.description ?? 'low');
            const draftHours = lowConfHoursDraft[itemKey] ?? hours ?? null;
            const saveChecked = saveToLibraryChecked[itemKey] !== false; // default true

            const vehicleLabel = vehicle
              ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.engine_size ? ` ${vehicle.engine_size}` : ''}`
              : null;

            const handleConfirm = async () => {
              const finalHours = draftHours ?? hours;
              if (!finalHours || finalHours <= 0) {
                message.error('Enter valid labor hours before confirming.');
                return;
              }

              // Update the item with confirmed hours and mark as approved
              const updatedItems = items.map(i => {
                if ((i.id ?? i.description) === (record.id ?? record.description)) {
                  const baseLaborCost = finalHours * (i.hourly_rate || 0);
                  const discAmt = i.labor_discount_amount || (baseLaborCost * (i.labor_discount_percentage || 0) / 100);
                  const partsCost = (i.parts || []).reduce((s, p) => s + (p.discounted_price || p.total_price), 0);
                  return {
                    ...i,
                    labor_hours: finalHours,
                    labor_confidence: 'verified' as const,
                    approval_status: 'approved' as const,
                    total_price: (baseLaborCost - discAmt) + partsCost,
                  };
                }
                return i;
              });
              onChange(updatedItems);

              // Save to library if checkbox checked
              if (saveChecked && vehicle?.make && vehicle?.model) {
                const saved = await saveLaborToLibrary(record, finalHours);
                if (saved === false && saveChecked) {
                  // Try fetch result — if 409 it means duplicate
                  const { getTokens } = await import('../services/authApi');
                  const tokens = getTokens();
                  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                  if (tokens?.accessToken) headers['Authorization'] = `Bearer ${tokens.accessToken}`;
                  const apiUrl = process.env.REACT_APP_API_URL ?? '';
                  const checkResp = await fetch(`${apiUrl}/admin/labor-times`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      category: (record.category || 'other').toLowerCase().replace(/\s+/g, '_'),
                      service_description: record.description,
                      vehicle_make: vehicle?.make,
                      vehicle_model: vehicle?.model,
                      vehicle_year_from: vehicle?.year,
                      vehicle_year_to: vehicle?.year,
                      engine_size: vehicle?.engine_size || null,
                      transmission_type: null,
                      flat_rate_hours: finalHours,
                      notes: 'Advisor-confirmed from estimate',
                    }),
                  });
                  if (checkResp.status === 409) {
                    message.info('This job already exists in the library. Hours updated on this estimate only.');
                  } else if (checkResp.ok) {
                    message.success(`Saved ${finalHours}h for ${record.description} to library.`);
                  } else {
                    message.warning('Hours confirmed. Library save failed — try again from the Labor Times admin page.');
                  }
                } else if (saved) {
                  message.success(`Saved ${finalHours}h for ${record.description} to library.`);
                }
              } else {
                message.success(`Labor hours confirmed: ${finalHours}h`);
              }

              // Cleanup draft state
              setLowConfHoursDraft(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
              setSaveToLibraryChecked(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
            };

            return (
              <div style={{ lineHeight: 1.4 }}>
                <Tooltip title={tooltipText} placement="right">
                  <span style={{ fontSize: '10px', color: badgeColor, fontWeight: 600, cursor: 'help', display: 'block' }}>
                    {badgeLabel}
                  </span>
                </Tooltip>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <InputNumber
                    size="small"
                    min={0.1}
                    max={40}
                    step={0.1}
                    precision={1}
                    value={draftHours ?? undefined}
                    onChange={val => setLowConfHoursDraft(prev => ({ ...prev, [itemKey]: val ?? null }))}
                    style={{ width: 64 }}
                    placeholder="hrs"
                  />
                  <Tooltip title="Confirm hours and approve item">
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleConfirm}
                      disabled={disabled}
                      style={{ padding: '0 6px' }}
                    />
                  </Tooltip>
                </div>
                {vehicleLabel && (
                  <div style={{ marginTop: 4 }}>
                    <Checkbox
                      checked={saveChecked}
                      onChange={e => setSaveToLibraryChecked(prev => ({ ...prev, [itemKey]: e.target.checked }))}
                      style={{ fontSize: '10px' }}
                    >
                      <span style={{ fontSize: '10px', color: '#6b7280' }}>
                        Save {draftHours ? `${draftHours}h` : ''} for {vehicleLabel} to library
                      </span>
                    </Checkbox>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontWeight: 500 }}>{hoursDisplay}</div>
              <Tooltip title={tooltipText} placement="right">
                <span
                  style={{
                    fontSize: '10px',
                    color: badgeColor,
                    cursor: 'help',
                    whiteSpace: 'nowrap',
                    fontWeight: 400,
                  }}
                >
                  {badgeLabel}
                </span>
              </Tooltip>
              {record.procedure_steps && record.procedure_steps.length > 0 && (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    fontSize: '11px',
                    color: '#6366f1',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '10px',
                    padding: '1px 6px',
                    marginTop: '2px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowKeys((prev: string[]) =>
                      prev.includes(String(record.id))
                        ? prev.filter((k) => k !== String(record.id))
                        : [...prev, String(record.id)]
                    );
                  }}
                  title="Click to view procedure steps"
                >
                  {record.procedure_steps.length} steps
                </div>
              )}
            </div>
          );
        }

        return (
          <div>
            <span>{hoursDisplay}</span>
            {record.procedure_steps && record.procedure_steps.length > 0 && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '11px',
                  color: '#6366f1',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '10px',
                  padding: '1px 6px',
                  marginTop: '2px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedRowKeys((prev: string[]) =>
                    prev.includes(String(record.id))
                      ? prev.filter((k) => k !== String(record.id))
                      : [...prev, String(record.id)]
                  );
                }}
                title="Click to view procedure steps"
              >
                {record.procedure_steps.length} steps
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Rate',
      dataIndex: 'hourly_rate',
      width: isMobile ? 60 : 80,
      render: (rate: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        return rate ? `$${rate}/hr` : '—';
      },
    },
    ...(!isMobile ? [{
      title: 'Labor Disc',
      dataIndex: 'labor_discount',
      width: 95,
      render: (_: any, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }

        const hasDiscount = record.labor_discount_percentage || record.labor_discount_amount;
        if (!hasDiscount) return '—';

        return (
          <div style={{ fontSize: '11px' }}>
            {record.labor_discount_percentage && (
              <div style={{ color: '#ef4444' }}>{record.labor_discount_percentage.toFixed(1)}%</div>
            )}
            {record.labor_discount_amount && (
              <div style={{ color: '#ef4444' }}>-{formatCurrency(record.labor_discount_amount).substring(1)}</div>
            )}
          </div>
        );
      },
    }] : []),
    {
      title: 'Total',
      dataIndex: 'total_price',
      width: isMobile ? 75 : 95,
      render: (total: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing (shows total in the edit form)
        }

        const baseLaborCost = (record.labor_hours || 0) * (record.hourly_rate || 0);
        const laborDiscountAmount = record.labor_discount_amount ||
          (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;
        const partsCost = (record.parts || []).reduce((sum, part) => sum + (part.discounted_price || part.total_price), 0);

        if (baseLaborCost > 0 && partsCost > 0) {
          return (
            <div>
              <Text strong style={{ color: '#10b981' }}>
                {formatCurrency(total)}
              </Text>
              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.2' }}>
                <div>
                  Labor: {laborDiscountAmount > 0 ? (
                    <>
                      <span style={{ textDecoration: 'line-through' }}>{formatCurrency(baseLaborCost)}</span>
                      <span style={{ color: '#10b981', marginLeft: '4px' }}>{formatCurrency(discountedLaborCost)}</span>
                    </>
                  ) : (
                    formatCurrency(baseLaborCost)
                  )}
                </div>
                <div>Parts: {formatCurrency(partsCost)}</div>
              </div>
            </div>
          );
        }

        if (baseLaborCost > 0) {
          return (
            <div>
              <Text strong style={{ color: '#10b981' }}>
                {formatCurrency(total)}
              </Text>
              {laborDiscountAmount > 0 && (
                <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.2' }}>
                  <span style={{ textDecoration: 'line-through' }}>{formatCurrency(baseLaborCost)}</span>
                  <span style={{ color: '#10b981', marginLeft: '4px' }}>{formatCurrency(discountedLaborCost)}</span>
                </div>
              )}
            </div>
          );
        }

        return (
          <Text strong style={{ color: '#10b981' }}>
            {formatCurrency(total)}
          </Text>
        );
      },
    },
    {
      title: 'Actions',
      width: isMobile ? 75 : 90,
      fixed: isMobile ? undefined : ('right' as const),
      render: (text: any, record: EstimateLineItem) => {
        const editable = isEditing(record);
        const recordKey = (record.id || 'new').toString();

        if (editable) {
          return null; // Custom row handles editing (shows Save/Cancel buttons)
        }

        // Pending AI-suggested items get approve/edit/delete buttons instead of the menu
        if (record.approval_status === 'pending') {
          return (
            <Space size={2}>
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                title="Approve — add to estimate"
                onClick={() => approveItem(recordKey)}
                style={{ color: '#10b981', padding: '0 4px' }}
                disabled={disabled}
              />
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                title="Edit this service item"
                onClick={() => edit(record)}
                style={{ color: '#1677ff', padding: '0 4px' }}
                disabled={disabled}
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                title="Remove this AI suggestion"
                onClick={() => deleteItem(recordKey)}
                style={{ color: '#ef4444', padding: '0 4px' }}
                disabled={disabled}
              />
            </Space>
          );
        }

        const handleMenuClick = (e: { key: string }) => {
          switch (e.key) {
            case 'edit':
              edit(record);
              break;
            case 'save-template':
              saveLineItemAsTemplate(record);
              break;
            case 'delete':
              cancel();
              setItemToDelete(recordKey);
              setDeleteItemModalVisible(true);
              break;
          }
        };

        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
          },
          {
            key: 'save-template',
            icon: <SaveOutlined />,
            label: 'Save as Template',
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={['click']}
            disabled={disabled}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              disabled={disabled}
            />
          </Dropdown>
        );
      },
    },
    Table.EXPAND_COLUMN,
  ], [isMobile, editingKey, items, disabled, vehicle, lowConfHoursDraft, saveToLibraryChecked, expandedRowKeys, discountType, defaultLaborRate]);

  const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;
  const pendingCount = items.filter(i => i.approval_status === 'pending').length;

  return (
    <div>
      <Card
        title={
          <Space>
            <span>Service Items</span>
            {pendingCount > 0 && (
              <Tag icon={<RobotOutlined />} color="blue" style={{ fontWeight: 400 }}>
                {pendingCount} AI suggestion{pendingCount !== 1 ? 's' : ''}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            {pendingCount > 0 && (
              <>
                <Button
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={approveAllPending}
                  disabled={disabled}
                  style={{ color: '#10b981', borderColor: '#10b981' }}
                >
                  Approve All
                </Button>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={deleteAllPending}
                  disabled={disabled}
                  danger
                >
                  Delete Pending
                </Button>
              </>
            )}
            <Button
              type="default"
              icon={<StarOutlined />}
              onClick={() => setSavedTemplateModalVisible(true)}
              disabled={disabled}
              style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
            >
              My Templates
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addNewItem}
              disabled={disabled}
            >
              Add Custom Item
            </Button>
          </Space>
        }
        style={{ marginBottom: '24px' }}
        headStyle={{
          position: 'sticky',
          top: '-24px',
          zIndex: 10,
          backgroundColor: '#fff',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <style>{`
          .service-item-row td {
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            vertical-align: top !important;
            min-height: auto;
          }
          .service-item-row .ant-table-cell {
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            vertical-align: top !important;
            white-space: normal !important;
            word-wrap: break-word !important;
          }
          .ai-pending-row td {
            background-color: #f0f7ff !important;
          }
          .ai-pending-row:hover td {
            background-color: #e6f0fd !important;
          }
          .editing-row td {
            border-bottom: 2px solid #3b82f6 !important;
          }
          .editing-row .ant-form-item-label {
            padding-bottom: 4px !important;
          }
          .editing-row .ant-form-item-label > label {
            height: auto !important;
          }
        `}</style>
        <EditRowContext.Provider value={{
          items, editingKey, form, isMobile, disabled, discountType, setDiscountType,
          defaultLaborRate, save, cancel, recalculateLineTotalOnChange, columns,
        }}>
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={items}
            rowKey={record => String(record.id ?? record.description ?? 'new')}
            pagination={false}
            size="small"
            components={{
              body: {
                row: CustomRow,
              },
            }}
            scroll={{
              x: isMobile ? 500 : undefined,
              y: undefined
            }}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
              expandedRowRender: (record) => {
                return (
                  <div>
                    <PartsSection
                      lineItem={record}
                      onChange={(updatedLineItem) => {
                        const updatedItems = items.map(item =>
                          item.id === record.id ? updatedLineItem : item
                        );
                        onChange(updatedItems);
                      }}
                      disabled={disabled}
                      vehicle={vehicle}
                    />
                    {((record.procedure_steps?.length ?? 0) > 0 ||
                      (record.parts_researched?.length ?? 0) > 0 ||
                      (record.special_tools_researched?.length ?? 0) > 0) && (
                      <LineItemProcedureEditor
                        item={record}
                        itemIndex={items.findIndex((i) => i === record)}
                        onLaborHoursChange={(idx, newTotal) => {
                          const updated = items.map((it, i) =>
                            i === idx ? { ...it, labor_hours: newTotal } : it
                          );
                          onChange(updated);
                        }}
                        readOnly={disabled}
                      />
                    )}
                  </div>
                );
              },
              expandIcon: ({ expanded, onExpand, record }) => (
                <Button
                  type={expanded ? 'default' : 'primary'}
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => onExpand(record, e)}
                  style={{
                    backgroundColor: expanded ? '#f0f0f0' : '#1890ff',
                    borderColor: expanded ? '#d9d9d9' : '#1890ff',
                    color: expanded ? '#666' : '#fff',
                  }}
                >
                  {expanded ? 'Close' : 'Parts'}
                </Button>
              ),
              rowExpandable: () => true,
            }}
            locale={{
              emptyText: (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Text type="secondary">No items added yet</Text>
                  <br />
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={addNewItem}
                    disabled={disabled}
                  >
                    Add your first item
                  </Button>
                </div>
              ),
            }}
            rowClassName={(record) =>
              record.approval_status === 'pending'
                ? 'service-item-row ai-pending-row'
                : 'service-item-row'
            }
          />
        </Form>
        </EditRowContext.Provider>

      </Card>

      <LineItemSavedTemplateModal
        open={savedTemplateModalVisible}
        onCancel={() => setSavedTemplateModalVisible(false)}
        onSelect={addFromSavedTemplate}
        vehicle={vehicle}
      />
      <Modal
        title="Save Line Item as Template"
        open={saveLineItemTemplateModalVisible}
        onOk={handleSaveLineItemTemplate}
        onCancel={() => {
          setSaveLineItemTemplateModalVisible(false);
          setLineItemToSave(null);
          saveLineItemTemplateForm.resetFields();
        }}
        okText="Save Template"
        cancelText="Cancel"
        centered
        width={550}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Save this service item as a reusable template. You can use it later in any estimate.
          </Text>
        </div>
        {lineItemToSave && (
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '1px solid #e5e7eb'
          }}>
            <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Service Item Details:
            </Text>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              <div><strong>Category:</strong> {lineItemToSave.category}</div>
              <div><strong>Description:</strong> {lineItemToSave.description}</div>
              <div><strong>Quantity:</strong> {lineItemToSave.quantity}</div>
              {lineItemToSave.labor_hours && lineItemToSave.labor_hours > 0 && (
                <div><strong>Labor:</strong> {lineItemToSave.labor_hours}h @ ${lineItemToSave.hourly_rate}/hr</div>
              )}
              <div><strong>Total Price:</strong> {formatCurrency(lineItemToSave.total_price)}</div>
            </div>
          </div>
        )}
        <Form form={saveLineItemTemplateForm} layout="vertical">
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: 'Please enter a template name' },
              { min: 1, message: 'Template name must not be empty' }
            ]}
          >
            <Input placeholder="Enter a name for this template" autoFocus />
          </Form.Item>
          <Form.Item
            name="isGeneral"
            valuePropName="checked"
            help={!vehicle ? undefined : "If unchecked, this template will only appear when working with similar vehicles"}
          >
            <Checkbox>
              Make this a general template (available for all vehicles)
            </Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isGeneral !== currentValues.isGeneral}
          >
            {({ getFieldValue }) =>
              !getFieldValue('isGeneral') && vehicle ? (
                <Form.Item
                  name="vehicleYears"
                  label="Vehicle Years"
                  rules={[{ required: true, message: 'Please select at least one year' }]}
                  help="Select all years this service applies to"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select years"
                    showSearch
                    filterOption={(input, option) => {
                      const yearValue = option?.value;
                      if (typeof yearValue === 'number') {
                        return yearValue.toString().includes(input);
                      }
                      return false;
                    }}
                  >
                    {serviceItemsYearOptions.map(year => (
                      <Select.Option key={year} value={year}>
                        {year}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Delete Line Item"
        open={deleteItemModalVisible}
        onOk={handleConfirmDeleteItem}
        onCancel={() => {
          setDeleteItemModalVisible(false);
          setItemToDelete('');
        }}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        centered
      >
        <p>Are you sure you want to delete this line item? This action cannot be undone.</p>
      </Modal>

    </div>
  );
};

const PartSavedTemplateModal: React.FC<PartSavedTemplateModalProps> = ({
  open,
  onCancel,
  onSelect,
  vehicle,
}) => {
  const [templates, setTemplates] = useState<PartSavedTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<PartSavedTemplate | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await partSavedTemplateApi.getAll({ search: searchText });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching saved part templates:', error);
      message.error('Failed to load saved part templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await partSavedTemplateApi.delete(id);
      message.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  const handleEdit = (template: PartSavedTemplate) => {
    setEditingTemplate(template);
    // Parse vehicle_years from comma-separated string to array of numbers
    const years = template.vehicle_years ? template.vehicle_years.split(',').map(y => parseInt(y.trim())) : [];
    const isGeneral = !template.vehicle_years && !template.vehicle_make && !template.vehicle_model;

    editForm.setFieldsValue({
      name: template.name,
      part_number: template.part_number,
      description: template.description,
      unit_price: template.unit_price,
      discount_percentage: template.discount_percentage,
      discount_amount: template.discount_amount,
      availability: template.availability,
      supplier: template.supplier,
      notes: template.notes,
      isGeneral: isGeneral,
      vehicleYears: years,
      vehicle_make: template.vehicle_make,
      vehicle_model: template.vehicle_model,
    });
    setEditModalVisible(true);
  };

  const handleUpdateTemplate = async () => {
    try {
      if (!editingTemplate || !editingTemplate.id) return;

      const values = await editForm.validateFields();

      const updatedTemplate: Partial<PartSavedTemplate> = {
        name: values.name,
        part_number: values.part_number,
        description: values.description,
        unit_price: values.unit_price,
        discount_percentage: values.discount_percentage,
        discount_amount: values.discount_amount,
        availability: values.availability,
        supplier: values.supplier,
        notes: values.notes,
      };

      // Handle vehicle information
      if (values.isGeneral || !values.vehicleYears || values.vehicleYears.length === 0) {
        // Clear vehicle info for general templates
        updatedTemplate.vehicle_years = undefined;
        updatedTemplate.vehicle_make = undefined;
        updatedTemplate.vehicle_model = undefined;
      } else {
        // Set vehicle info for vehicle-specific templates
        updatedTemplate.vehicle_years = values.vehicleYears.join(',');
        updatedTemplate.vehicle_make = values.vehicle_make;
        updatedTemplate.vehicle_model = values.vehicle_model;
      }

      await partSavedTemplateApi.update(editingTemplate.id, updatedTemplate);
      message.success('Template updated successfully!');
      setEditModalVisible(false);
      setEditingTemplate(null);
      editForm.resetFields();
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      message.error('Failed to update template');
    }
  };

  const filteredTemplates = templates.filter(t => {
    // First filter by search text (including vehicle fields)
    const searchLower = searchText.toLowerCase();
    const matchesSearch = searchText === '' ||
      t.name.toLowerCase().includes(searchLower) ||
      t.description.toLowerCase().includes(searchLower) ||
      t.part_number.toLowerCase().includes(searchLower) ||
      (t.vehicle_years && t.vehicle_years.includes(searchText)) ||
      (t.vehicle_make && t.vehicle_make.toLowerCase().includes(searchLower)) ||
      (t.vehicle_model && t.vehicle_model.toLowerCase().includes(searchLower));

    if (!matchesSearch) return false;

    // If no vehicle context, show all templates
    if (!vehicle) return true;

    // Show general templates (templates without vehicle info)
    const isGeneralTemplate = !t.vehicle_years && !t.vehicle_make && !t.vehicle_model;
    if (isGeneralTemplate) return true;

    // Show vehicle-specific templates that match the current vehicle
    const years = t.vehicle_years ? t.vehicle_years.split(',').map(y => parseInt(y.trim())) : [];
    const matchesVehicle =
      years.includes(vehicle.year) &&
      t.vehicle_make === vehicle.make &&
      t.vehicle_model === vehicle.model;

    return matchesVehicle;
  });

  return (
    <Modal
      title="My Saved Part Templates"
      open={open}
      onCancel={onCancel}
      footer={null}
      width="100%"
      style={{ maxWidth: '900px', top: 20, margin: '0 auto' }}
      bodyStyle={{
        padding: '16px',
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto'
      }}
      centered={false}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
        width: '100%'
      }}>
        <div style={{ width: '100%', minHeight: '40px' }}>
          <Input
            placeholder="Search parts..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            style={{
              width: '100%',
              fontSize: '16px',
              display: 'block',
              visibility: 'visible'
            }}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : filteredTemplates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <StarOutlined style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }} />
          <Text>No saved part templates yet. Save a part as a template to see it here!</Text>
        </div>
      ) : (
        <Row gutter={[12, 12]}>
          {filteredTemplates.map(template => (
            <Col xs={24} sm={12} lg={8} key={template.id}>
                <Card
                  hoverable
                  size="small"
                  style={{ borderRadius: '8px', cursor: 'pointer', backgroundColor: '#fffbeb' }}
                  onClick={() => onSelect(template)}
                  extra={
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(template);
                        }}
                      />
                      <Popconfirm
                        title="Delete this template?"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          if (template.id) handleDelete(template.id);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </Space>
                  }
                >
                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ color: '#f59e0b', display: 'block', fontSize: '14px' }}>{template.name}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {template.part_number}
                    </Text>
                  </div>
                  {template.vehicle_years && template.vehicle_make && template.vehicle_model ? (
                    <div style={{
                      marginBottom: '8px',
                      padding: '6px 8px',
                      backgroundColor: '#f3e8ff',
                      borderRadius: '4px',
                      border: '1px solid #d8b4fe'
                    }}>
                      <Text strong style={{ fontSize: '11px', color: '#7c3aed', display: 'block' }}>
                        Vehicle Fitment:
                      </Text>
                      <Text style={{ fontSize: '12px', color: '#6b21a8' }}>
                        {template.vehicle_years} {template.vehicle_make} {template.vehicle_model}
                      </Text>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '8px' }}>
                      <Tag color="green" style={{ fontSize: '11px' }}>
                        General (All Vehicles)
                      </Tag>
                    </div>
                  )}
                  <Text style={{ fontSize: '12px', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    {template.description}
                  </Text>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tag color="blue" style={{ fontSize: '11px' }}>
                      {template.availability}
                    </Tag>
                    <Text strong style={{ color: '#f59e0b' }}>
                      {formatCurrency(template.unit_price)}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
      )}

      {/* Edit Part Template Modal */}
      <Modal
        title="Edit Part Template"
        open={editModalVisible}
        onOk={handleUpdateTemplate}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingTemplate(null);
          editForm.resetFields();
        }}
        okText="Update Template"
        cancelText="Cancel"
        centered
        width={600}
      >
        {editingTemplate && (
          <Form form={editForm} layout="vertical">
            <Form.Item
              name="name"
              label="Template Name"
              rules={[{ required: true, message: 'Please enter a template name' }]}
            >
              <Input placeholder="Enter template name" />
            </Form.Item>
            <Form.Item
              name="part_number"
              label="Part Number"
              rules={[{ required: true, message: 'Please enter part number' }]}
            >
              <Input placeholder="P12345" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <Input.TextArea placeholder="Part description" rows={2} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="unit_price"
                  label="Unit Price"
                  rules={[{ required: true, message: 'Please enter price' }]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                    prefix="$"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="availability"
                  label="Availability"
                  rules={[{ required: true, message: 'Please select availability' }]}
                >
                  <Select>
                    <Select.Option value="in_stock">In Stock</Select.Option>
                    <Select.Option value="order_required">Order Required</Select.Option>
                    <Select.Option value="backordered">Backordered</Select.Option>
                    <Select.Option value="discontinued">Discontinued</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="discount_percentage" label="Discount %">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="discount_amount" label="Discount $">
                  <InputNumber min={0} step={0.01} style={{ width: '100%' }} prefix="$" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="supplier" label="Supplier">
              <Input placeholder="Supplier name" />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea placeholder="Additional notes" rows={2} />
            </Form.Item>
            <Form.Item name="isGeneral" valuePropName="checked">
              <Checkbox>Make this a general template (available for all vehicles)</Checkbox>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.isGeneral !== currentValues.isGeneral}
            >
              {({ getFieldValue }) =>
                !getFieldValue('isGeneral') && (
                  <>
                    <Form.Item
                      name="vehicleYears"
                      label="Vehicle Years"
                      rules={[
                        {
                          required: !getFieldValue('isGeneral'),
                          message: 'Please select at least one year for vehicle-specific templates'
                        }
                      ]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select years (e.g., 2006, 2007, 2008)"
                        style={{ width: '100%' }}
                        showSearch
                        filterOption={(input, option) => {
                          const yearValue = option?.value;
                          if (typeof yearValue === 'number') {
                            return yearValue.toString().includes(input);
                          }
                          return false;
                        }}
                      >
                        {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <Select.Option key={year} value={year}>
                            {year}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="vehicle_make"
                          label="Vehicle Make"
                          rules={[
                            {
                              required: !getFieldValue('isGeneral'),
                              message: 'Please enter vehicle make'
                            }
                          ]}
                        >
                          <Input placeholder="e.g., Toyota" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="vehicle_model"
                          label="Vehicle Model"
                          rules={[
                            {
                              required: !getFieldValue('isGeneral'),
                              message: 'Please enter vehicle model'
                            }
                          ]}
                        >
                          <Input placeholder="e.g., Camry" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )
              }
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Modal>
  );
};

const LineItemSavedTemplateModal: React.FC<LineItemSavedTemplateModalProps> = ({
  open,
  onCancel,
  onSelect,
  vehicle,
}) => {
  const [templates, setTemplates] = useState<LineItemTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<LineItemTemplate | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  useEffect(() => {
    if (open) {
      fetchTemplates();
      fetchCategories();
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await lineItemTemplateApi.getAll({
        search: searchText,
        category: selectedCategory === 'all' ? undefined : selectedCategory
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching saved line item templates:', error);
      message.error('Failed to load saved line item templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await lineItemTemplateApi.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await lineItemTemplateApi.delete(id);
      message.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  const handleEdit = (template: LineItemTemplate) => {
    setEditingTemplate(template);
    // Parse vehicle_years from comma-separated string to array of numbers
    const years = template.vehicle_years ? template.vehicle_years.split(',').map(y => parseInt(y.trim())) : [];
    const isGeneral = !template.vehicle_years && !template.vehicle_make && !template.vehicle_model;

    editForm.setFieldsValue({
      name: template.name,
      category: template.category,
      description: template.description,
      quantity: template.quantity,
      labor_hours: template.labor_hours,
      hourly_rate: template.hourly_rate,
      labor_discount_percentage: template.labor_discount_percentage,
      labor_discount_amount: template.labor_discount_amount,
      notes: template.notes,
      isGeneral: isGeneral,
      vehicleYears: years,
      vehicle_make: template.vehicle_make,
      vehicle_model: template.vehicle_model,
    });
    setEditModalVisible(true);
  };

  const handleUpdateTemplate = async () => {
    try {
      if (!editingTemplate || !editingTemplate.id) return;

      const values = await editForm.validateFields();

      const updatedTemplate: Partial<LineItemTemplate> = {
        name: values.name,
        category: values.category,
        description: values.description,
        quantity: values.quantity,
        labor_hours: values.labor_hours,
        hourly_rate: values.hourly_rate,
        labor_discount_percentage: values.labor_discount_percentage,
        labor_discount_amount: values.labor_discount_amount,
        notes: values.notes,
      };

      // Handle vehicle information
      if (values.isGeneral || !values.vehicleYears || values.vehicleYears.length === 0) {
        // Clear vehicle info for general templates
        updatedTemplate.vehicle_years = undefined;
        updatedTemplate.vehicle_make = undefined;
        updatedTemplate.vehicle_model = undefined;
      } else {
        // Set vehicle info for vehicle-specific templates
        updatedTemplate.vehicle_years = values.vehicleYears.join(',');
        updatedTemplate.vehicle_make = values.vehicle_make;
        updatedTemplate.vehicle_model = values.vehicle_model;
      }

      await lineItemTemplateApi.update(editingTemplate.id, updatedTemplate);
      message.success('Template updated successfully!');
      setEditModalVisible(false);
      setEditingTemplate(null);
      editForm.resetFields();
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      message.error('Failed to update template');
    }
  };

  const filteredTemplates = templates.filter(t => {
    // First filter by search text (including vehicle fields)
    const searchLower = searchText.toLowerCase();
    const matchesSearch = searchText === '' ||
      t.name.toLowerCase().includes(searchLower) ||
      t.description.toLowerCase().includes(searchLower) ||
      t.category.toLowerCase().includes(searchLower) ||
      (t.vehicle_years && t.vehicle_years.includes(searchText)) ||
      (t.vehicle_make && t.vehicle_make.toLowerCase().includes(searchLower)) ||
      (t.vehicle_model && t.vehicle_model.toLowerCase().includes(searchLower));

    if (!matchesSearch) return false;

    // If no vehicle context, show all templates
    if (!vehicle) return true;

    // Show general templates (templates without vehicle info)
    const isGeneralTemplate = !t.vehicle_years && !t.vehicle_make && !t.vehicle_model;
    if (isGeneralTemplate) return true;

    // Show vehicle-specific templates that match the current vehicle
    const years = t.vehicle_years ? t.vehicle_years.split(',').map(y => parseInt(y.trim())) : [];
    const matchesVehicle =
      years.includes(vehicle.year) &&
      t.vehicle_make === vehicle.make &&
      t.vehicle_model === vehicle.model;

    return matchesVehicle;
  });

  return (
    <Modal
      title="My Saved Line Item Templates"
      open={open}
      onCancel={onCancel}
      footer={null}
      width="100%"
      style={{ maxWidth: '900px', top: 20, margin: '0 auto' }}
      bodyStyle={{
        padding: '16px',
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto'
      }}
      centered={false}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
        width: '100%'
      }}>
        <div style={{ width: '100%', minHeight: '40px' }}>
          <Input
            placeholder="Search services..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            style={{
              width: '100%',
              fontSize: '16px',
              display: 'block',
              visibility: 'visible'
            }}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
          />
        </div>
        <div style={{ width: '100%', minHeight: '40px' }}>
          <Select
            value={selectedCategory}
            onChange={(value) => {
              setSelectedCategory(value);
              fetchTemplates();
            }}
            style={{
              width: '100%',
              fontSize: '16px',
              display: 'block'
            }}
            placeholder="Filter by category"
            size="large"
          >
            <Select.Option value="all">All Categories</Select.Option>
            {categories.map(category => (
              <Select.Option key={category} value={category}>
                {category}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : filteredTemplates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <StarOutlined style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }} />
          <Text>No saved line item templates yet. Save a line item as a template to see it here!</Text>
        </div>
      ) : (
        <Row gutter={[12, 12]}>
          {filteredTemplates.map(template => (
            <Col xs={24} sm={12} lg={8} key={template.id}>
                <Card
                  hoverable
                  size="small"
                  style={{ borderRadius: '8px', cursor: 'pointer', backgroundColor: '#fffbeb' }}
                  onClick={() => onSelect(template)}
                  extra={
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(template);
                        }}
                      />
                      <Popconfirm
                        title="Delete this template?"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          if (template.id) handleDelete(template.id);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </Space>
                  }
                >
                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ color: '#f59e0b', display: 'block', fontSize: '14px' }}>{template.name}</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Tag color="blue" style={{ fontSize: '11px' }}>
                        {template.category}
                      </Tag>
                    </div>
                  </div>
                  {template.vehicle_years && template.vehicle_make && template.vehicle_model ? (
                    <div style={{
                      marginBottom: '8px',
                      padding: '6px 8px',
                      backgroundColor: '#f3e8ff',
                      borderRadius: '4px',
                      border: '1px solid #d8b4fe'
                    }}>
                      <Text strong style={{ fontSize: '11px', color: '#7c3aed', display: 'block' }}>
                        Vehicle Fitment:
                      </Text>
                      <Text style={{ fontSize: '12px', color: '#6b21a8' }}>
                        {template.vehicle_years} {template.vehicle_make} {template.vehicle_model}
                      </Text>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '8px' }}>
                      <Tag color="green" style={{ fontSize: '11px' }}>
                        General (All Vehicles)
                      </Tag>
                    </div>
                  )}
                  <Text style={{ fontSize: '12px', color: '#374151', display: 'block', marginBottom: '8px' }}>
                    {template.description}
                  </Text>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                    <div>
                      {template.labor_hours && <div>Labor: {template.labor_hours}h @ ${template.hourly_rate}/hr</div>}
                      <div>Qty: {template.quantity}</div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
      )}

      {/* Edit Line Item Template Modal */}
      <Modal
        title="Edit Line Item Template"
        open={editModalVisible}
        onOk={handleUpdateTemplate}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingTemplate(null);
          editForm.resetFields();
        }}
        okText="Update Template"
        width={600}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter a template name' }]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {categories.map(category => (
                <Select.Option key={category} value={category}>
                  {category}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="Quantity" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="labor_hours" label="Labor Hours">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="Hours" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="hourly_rate" label="Hourly Rate">
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="Rate"
                  prefix="$"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="labor_discount_percentage" label="Labor Discount %">
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="Discount %"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="labor_discount_amount" label="Labor Discount Amount">
            <InputNumber
              min={0}
              step={1}
              style={{ width: '100%' }}
              placeholder="Discount Amount"
              prefix="$"
            />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Additional notes (optional)" />
          </Form.Item>

          <Form.Item name="isGeneral" valuePropName="checked">
            <Checkbox>General template (not vehicle-specific)</Checkbox>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isGeneral !== currentValues.isGeneral}
          >
            {({ getFieldValue }) =>
              !getFieldValue('isGeneral') && (
                <>
                  <Form.Item
                    name="vehicleYears"
                    label="Vehicle Years"
                    rules={[
                      {
                        required: !getFieldValue('isGeneral'),
                        message: 'Please select at least one year for vehicle-specific templates'
                      }
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select years (e.g., 2006, 2007, 2008)"
                      style={{ width: '100%' }}
                      showSearch
                      filterOption={(input, option) => {
                        const yearValue = option?.value;
                        if (typeof yearValue === 'number') {
                          return yearValue.toString().includes(input);
                        }
                        return false;
                      }}
                    >
                      {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <Select.Option key={year} value={year}>
                          {year}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="vehicle_make"
                        label="Vehicle Make"
                        rules={[
                          {
                            required: !getFieldValue('isGeneral'),
                            message: 'Please enter vehicle make'
                          }
                        ]}
                      >
                        <Input placeholder="e.g., Toyota" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="vehicle_model"
                        label="Vehicle Model"
                        rules={[
                          {
                            required: !getFieldValue('isGeneral'),
                            message: 'Please enter vehicle model'
                          }
                        ]}
                      >
                        <Input placeholder="e.g., Camry" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            }
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default ServiceItemsTable;